import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import './MarginsPage.css';

const fmt = (n, digits = 2) =>
  '₹' + (Number(n) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const pct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

export default function MarginsPage() {
  const [tab, setTab] = useState('products');
  const [withGst, setWithGst] = useState(false);
  const [margins, setMargins] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('margin');
  const [category, setCategory] = useState('All');

  useEffect(() => {
    setLoading(true);
    setError('');
    const q = withGst ? '?gst=1' : '';
    Promise.all([api.get('/reports/margins' + q), api.get('/reports/stock-value' + q)])
      .then(([m, s]) => { setMargins(m); setStock(s); })
      .catch((e) => setError(e.message || 'Could not load the report'))
      .finally(() => setLoading(false));
  }, [withGst]);

  const categories = useMemo(() => {
    const src = tab === 'products' ? margins : stock;
    return ['All', ...new Set(src.map((r) => r.category || 'Uncategorised'))];
  }, [tab, margins, stock]);

  useEffect(() => { setCategory('All'); }, [tab]);

  const term = search.trim().toLowerCase();

  const productRows = useMemo(() => {
    const rows = margins.filter((r) =>
      (!term || r.name.toLowerCase().includes(term)) &&
      (category === 'All' || (r.category || 'Uncategorised') === category));
    const key = {
      margin: (r) => (r.margin == null ? 999 : r.margin),
      profit30: (r) => -(r.profit_30d || 0),
      sold: (r) => -(r.sold_30d || 0),
      name: (r) => r.name,
    }[sortBy];
    return [...rows].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return typeof ka === 'string' ? ka.localeCompare(kb) : ka - kb;
    });
  }, [margins, term, category, sortBy]);

  const stockRows = useMemo(() => stock.filter((r) =>
    (!term || r.name.toLowerCase().includes(term)) &&
    (category === 'All' || (r.category || 'Uncategorised') === category)), [stock, term, category]);

  const productSummary = useMemo(() => {
    const costed = margins.filter((r) => r.margin != null);
    const sold = costed.filter((r) => r.sold_30d > 0);
    const revenue = sold.reduce((s, r) => s + r.revenue_30d, 0);
    const profit = sold.reduce((s, r) => s + r.profit_30d, 0);
    return {
      avg: costed.length ? costed.reduce((s, r) => s + r.margin, 0) / costed.length : null,
      weighted: revenue > 0 ? (profit / revenue) * 100 : null,
      loss: costed.filter((r) => r.margin < 0).length,
      low: costed.filter((r) => r.margin >= 0 && r.margin < 30).length,
      noCost: margins.filter((r) => r.margin == null).length,
    };
  }, [margins]);

  const stockByCategory = useMemo(() => {
    const map = {};
    stockRows.forEach((r) => {
      const c = r.category || 'Uncategorised';
      map[c] = (map[c] || 0) + (r.value || 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [stockRows]);

  const stockTotal = stock.reduce((s, r) => s + (r.value || 0), 0);
  const stockNoCost = stock.filter((r) => r.rate == null && r.current_stock > 0).length;

  const marginClass = (r) => {
    if (r.margin == null) return 'is-nocost';
    if (r.margin < 0) return 'is-loss';
    if (r.margin < 30) return 'is-low';
    return 'is-good';
  };

  return (
    <div className="mg-root">
      <header className="mg-head">
        <div>
          <h1>Margins &amp; stock value</h1>
          <p>Costs use the last purchase rate of each raw material, the same way sales are costed.</p>
        </div>
        <label className="mg-switch">
          <input type="checkbox" checked={withGst} onChange={(e) => setWithGst(e.target.checked)} />
          <span className="mg-switch-track" aria-hidden="true" />
          Include GST in cost
        </label>
        <button type="button" className="mg-print-btn" onClick={() => window.print()}>Print</button>
      </header>

      <div className="mg-print-title">
        Ice Bay x Hendy's Kitchen: {tab === 'products' ? 'Product margins' : 'Stock value'}
        {withGst ? ' (cost incl. GST)' : ''}, printed {new Date().toLocaleString('en-IN')}
      </div>

      <div className="mg-tabs" role="tablist">
        <button type="button" className={tab === 'products' ? 'is-active' : ''} onClick={() => setTab('products')}>
          Product margins
        </button>
        <button type="button" className={tab === 'stock' ? 'is-active' : ''} onClick={() => setTab('stock')}>
          Stock value
        </button>
      </div>

      {error && <div className="mg-error">{error}</div>}

      {tab === 'products' && (
        <div className="mg-summary">
          <div><span>Average margin</span><strong>{pct(productSummary.avg)}</strong></div>
          <div><span>Margin on last 30 days' sales</span><strong>{pct(productSummary.weighted)}</strong></div>
          <div className={productSummary.loss ? 'is-loss' : ''}><span>Selling at a loss</span><strong>{productSummary.loss}</strong></div>
          <div className={productSummary.low ? 'is-low' : ''}><span>Under 30% margin</span><strong>{productSummary.low}</strong></div>
          <div className={productSummary.noCost ? 'is-nocost' : ''}><span>No cost yet</span><strong>{productSummary.noCost}</strong></div>
        </div>
      )}

      {tab === 'stock' && (
        <div className="mg-summary">
          <div className="mg-summary-main"><span>Total stock value</span><strong>{fmt(stockTotal, 0)}</strong></div>
          {stockByCategory.slice(0, 4).map(([c, v]) => (
            <div key={c}><span>{c}</span><strong>{fmt(v, 0)}</strong></div>
          ))}
          {stockNoCost > 0 && (
            <div className="is-nocost"><span>In stock, no cost yet</span><strong>{stockNoCost}</strong></div>
          )}
        </div>
      )}

      <div className="mg-toolbar">
        <input
          type="text"
          placeholder={tab === 'products' ? 'Search products' : 'Search raw materials'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {tab === 'products' && (
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="margin">Lowest margin first</option>
            <option value="profit30">Most profit (30 days)</option>
            <option value="sold">Best sellers (30 days)</option>
            <option value="name">Name</option>
          </select>
        )}
      </div>

      {loading ? (
        <div className="mg-empty">Loading…</div>
      ) : tab === 'products' ? (
        <div className="mg-table-wrap">
          <table className="mg-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Price</th>
                <th className="num">Cost</th>
                <th className="num">Profit / unit</th>
                <th className="num">Margin</th>
                <th className="num">Sold (30d)</th>
                <th className="num">Profit (30d)</th>
              </tr>
            </thead>
            <tbody>
              {productRows.map((r) => (
                <tr key={r.id} className={marginClass(r)}>
                  <td>
                    <strong>{r.name}</strong>
                    <span className="mg-sub">
                      {r.category || 'Uncategorised'}
                      {r.weighted && ', per gram'}
                      {r.missing.length > 0 && `, no purchase yet for ${r.missing.join(', ')}`}
                    </span>
                  </td>
                  <td className="num">{fmt(r.price, r.weighted ? 3 : 2)}</td>
                  <td className="num">{r.margin == null ? '—' : fmt(r.cost, r.weighted ? 3 : 2)}</td>
                  <td className="num">{r.margin == null ? '—' : fmt(r.profit, r.weighted ? 3 : 2)}</td>
                  <td className="num"><span className="mg-pill">{r.margin == null ? 'No cost yet' : pct(r.margin)}</span></td>
                  <td className="num">{r.sold_30d ? (r.weighted ? `${r.sold_30d} g` : r.sold_30d) : '—'}</td>
                  <td className="num">{r.sold_30d && r.margin != null ? fmt(r.profit_30d, 0) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {productRows.length === 0 && <div className="mg-empty">No products match.</div>}
        </div>
      ) : (
        <div className="mg-table-wrap">
          <table className="mg-table">
            <thead>
              <tr>
                <th>Raw material</th>
                <th className="num">In stock</th>
                <th className="num">Last rate</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {stockRows.map((r) => (
                <tr key={r.id} className={r.rate == null && r.current_stock > 0 ? 'is-nocost' : ''}>
                  <td>
                    <strong>{r.name}</strong>
                    <span className="mg-sub">{r.category || 'Uncategorised'}</span>
                  </td>
                  <td className="num">{Number(r.current_stock).toLocaleString('en-IN')} {r.unit}</td>
                  <td className="num">{r.rate == null ? 'No purchase yet' : `${fmt(r.rate, r.unit === 'g' ? 4 : 2)}/${r.unit}`}</td>
                  <td className="num">{r.rate == null ? '—' : fmt(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {stockRows.length === 0 && <div className="mg-empty">No raw materials match.</div>}
        </div>
      )}
    </div>
  );
}
